import React, { Fragment, useState } from 'react';
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from '@headlessui/react';
import { useNavigate, useParams } from 'react-router-dom';

import BottomSheet from './BottomSheet/BottomSheet';
import SuccessModal from '../../FeedbackModal/SuccessModal';

import { useWidgetsModalContext } from '../../../../shared/providers/WidgetsModalProvider';
import useAuth from '../../../../utilities/hooks/useAuth';
import {
  HeaderItemKey,
  NavBarHeaderItems,
} from '../../../../shared/constants/NavBarItems';
import useLogout from '../../../../utilities/common/hooks/useLogout';

import { RenderNavigationHeaderItems } from '../../../../shared/types/NavBarItem';

import profileIcon from '../../../../assets/icons/Header/user-profile.svg';
import useTracking from '../../../../utilities/hooks/useTracking';
import {
  EventActions,
  EventNames,
} from '../../../../shared/constants/analytics';
import { UserIcon } from '../../../../assets/icons/User';
import { ChevronBottomIcon } from '../../../../assets/icons/ChevronBottomIcon';
import { Typography } from '../../../widgets/Typography';

export default function ProfileDropdown() {
  const { trackEvent } = useTracking();
  const { slug } = useParams();

  const [successOpen, setSuccessOpen] = useState<boolean>(false);
  const [show, setShow] = React.useState(false);

  const { logout } = useLogout();
  const { user, selfData } = useAuth();
  const { setFeedbackPopupOpen, setShowDownloadReport } =
    useWidgetsModalContext();

  const navigate = useNavigate();

  const path = slug ? slug!.replace(/[\s-]/g, '_').toLowerCase() : '';

  const visibleHeaderItems = NavBarHeaderItems.filter(
    (item) =>
      !item.visible || (selfData?.data?.config !== undefined && selfData?.data?.config !== null && item.visible(selfData.data.config)),
  );

  const instrumentItemClick = (eventLabel: string, eventSource: string) => {
    trackEvent(EventNames.WellBeingDashboard, {
      eventLabel,
      eventCategory: eventLabel,
      eventAction: EventActions.click,
      eventValue: path,
      eventSource,
    });
  };

  const handleShow = () => setShow(true);
  const handleHide = () => setShow(false);

  const renderProfileContent = (isMobile: boolean) => (
    <div
      className={`border-grey-100 flex items-center space-x-3 border-b ${
        isMobile ? 'p-5' : 'px-4 py-3'
      }`}
    >
      <div className="border-black-900/8 bg-grey-50 flex h-10 w-10 shrink-0 items-center justify-between rounded-full p-[8.33px]">
        <img
          className="m-auto h-[17.5px] w-[16.25px]"
          src={profileIcon}
          alt="Profile"
        />
      </div>
      <div className="min-w-0 flex-1">
        <Typography variant="body-sm" weight="medium">
          {user?.name}
        </Typography>
        <Typography
          variant="body-sm"
          className="text-grey-700 overflow-hidden text-ellipsis"
          title={user?.email}
        >
          {user?.email}
        </Typography>
      </div>
    </div>
  );

  const renderNavigationItems: RenderNavigationHeaderItems = (
    items,
    isMobile,
  ) => (
    <div className={`grid ${isMobile ? 'gap-y-4 py-2' : 'py-1'}`}>
      {items.map((navBarItem) => {
        const iconSize = isMobile ? 'h-5 w-5' : 'h-4 w-4';
        const Icon = navBarItem.icons;
        const iconClassName = `mr-2 ${iconSize} text-grey-500`;

        return (
          <React.Fragment key={navBarItem.key}>
            <button
              className={`group flex w-full cursor-pointer items-center py-2.5 text-left last:border-t last:py-3 ${
                isMobile ? 'px-5 text-base' : 'px-4 text-sm'
              } text-grey-700 hover:bg-black-100 hover:text-grey-900 font-normal transition-all duration-200`}
              onClick={() => {
                if (navBarItem.key === HeaderItemKey.GLOSSARY) {
                  instrumentItemClick('download_glossary', 'profile_bar');
                  window.open(navBarItem.link, '_blank');
                }
                if (navBarItem.key === HeaderItemKey.VIEW_USER_GUIDE) {
                  instrumentItemClick('download_user_guide', 'profile_bar');
                  window.open(navBarItem.link, '_blank');
                }
                if (navBarItem.key === HeaderItemKey.SIGNOUT) {
                  instrumentItemClick('sign_out', 'profile_bar');
                  logout();
                }
                if (navBarItem.key === HeaderItemKey.SUBMIT_FEEDBACK) {
                  setFeedbackPopupOpen(true);
                  instrumentItemClick('get_feedback', 'profile_bar');
                }
                if (navBarItem.key === HeaderItemKey.DOWNLOAD_REPORTS) {
                  setShowDownloadReport(true);
                  instrumentItemClick('download_report', 'profile_bar');
                }
                if (
                  navBarItem.key === HeaderItemKey.DIMENSION_PREVIOUS_VERSION
                ) {
                  if (navBarItem.link) {
                    instrumentItemClick('dimensions_v1', 'profile_bar');
                    navigate(navBarItem.link);
                  }
                }

                if (navBarItem.key === HeaderItemKey.MEMBER_MANAGEMENT) {
                  if (navBarItem.link) {
                    instrumentItemClick('member_management', 'profile_bar');
                    navigate(navBarItem.link);
                  }
                }
                handleHide();
              }}
            >
              {typeof Icon === 'string' ? (
                <img
                  src={Icon}
                  className={iconClassName}
                  alt=""
                  aria-hidden="true"
                />
              ) : (
                <Icon
                  className={`${iconClassName} stroke-current transition-colors duration-200`}
                  aria-hidden="true"
                />
              )}
              <span className="transition-colors duration-200">
                {navBarItem.label}
              </span>
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );

  return (
    <>
      <div className="lg:hidden">
        <div
          className={`border-black-900/8 bg-black-100 active:bg-black-300 flex h-10 w-10 items-center gap-x-2 rounded-full border-[0.75px] px-[11px] transition-all duration-150`}
          onClick={() => {
            handleShow();
            instrumentItemClick('user_profile', 'home_page');
          }}
        >
          <img
            className={`m-auto mr-2 h-[17.5px] w-[16.25px]`}
            src={profileIcon}
            alt="Profile"
          />
        </div>
        <BottomSheet show={show} onHide={handleHide}>
          {renderProfileContent(true)}
          {renderNavigationItems(visibleHeaderItems, true)}
        </BottomSheet>
      </div>

      <Menu as="div" className="relative hidden text-left lg:inline-block">
        <MenuButton
          onClick={() => instrumentItemClick('user_profile', 'home_page')}
          className="inline-flex min-w-fit items-center justify-center gap-2 rounded-[18px] border border-taupe-600 bg-white px-4 py-2.5 text-sm font-medium leading-5 text-grey-800 transition-all duration-200 ease-in-out hover:bg-gray-50 hover:text-black-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 active:border-black-200 active:bg-white active:shadow-[0px_0px_0px_4px_#F2F4F7]"
        >
          {({ open }) => (
            <>
              <span className="mr-1">
                <UserIcon />
              </span>
              <span>Account</span>
              <span className="ml-1">
                <ChevronBottomIcon
                  className={`stroke-grey-500 transition-transform duration-300 ease-out ${open ? 'rotate-180' : 'rotate-0'}`}
                />
              </span>
            </>
          )}
        </MenuButton>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <MenuItems className="border-black-200 absolute right-0 mt-2 w-60 origin-top-right rounded-lg border bg-white shadow-[0px_12px_16px_-4px_#10182814] outline-none">
            {renderProfileContent(false)}
            <MenuItem>
              {renderNavigationItems(
                visibleHeaderItems.filter((item) => item.showDesktop !== false),
                false,
              )}
            </MenuItem>
          </MenuItems>
        </Transition>
      </Menu>

      <SuccessModal open={successOpen} />
    </>
  );
}
